/** @file The open menu: items, roving focus, keyboard, viewport-aware position, dismiss and focus restore. */
import { Fragment, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import './tokens.css';
import './ContextMenu.css';
import { joinClass } from './logic/joinClass';
import { menuKey } from './logic/menuKey';
import { placeMenu, type Point } from './logic/placeMenu';
import { stepIndex } from './logic/stepIndex';
import { useMenuDismiss } from './useMenuDismiss';
import { useRestoreFocus } from './useRestoreFocus';

/** One menu entry; `label` is already-translated text. */
export interface MenuItem {
  id: string;
  label: string;
  disabled?: boolean;
  danger?: boolean;
  /** Draw a divider line above this item. */
  separatorBefore?: boolean;
}

/** Props of ContextMenuList. */
export interface ContextMenuListProps {
  at: Point;
  items: readonly MenuItem[];
  onSelect: (id: string) => void;
  onClose: () => void;
  label: string;
}

/** Mounted only while the menu is open, so hooks run once per opening. Every keydown is stopped while it is open; Enter and Space activate the focused item through the button's own click. */
export function ContextMenuList({ at, items, onSelect, onClose, label }: ContextMenuListProps) {
  const ref = useRef<HTMLDivElement>(null);
  const disabled = items.map((item) => item.disabled === true);
  const [active, setActive] = useState(() => stepIndex(-1, 1, disabled, false));
  useRestoreFocus(true);
  useMenuDismiss(ref, onClose);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const spot = placeMenu(at, { width: el.offsetWidth, height: el.offsetHeight }, { width: window.innerWidth, height: window.innerHeight });
    el.style.left = `${String(spot.x)}px`;
    el.style.top = `${String(spot.y)}px`;
  }, [at]);
  useLayoutEffect(() => {
    ref.current?.querySelectorAll<HTMLElement>('[role="menuitem"]')[active]?.focus();
  }, [active]);
  const onKeyDown = (e: KeyboardEvent<HTMLElement>): void => {
    e.stopPropagation();
    const action = menuKey(e.code, active, disabled);
    if (action.kind === 'none') return;
    e.preventDefault();
    if (action.kind === 'close') onClose();
    else setActive(action.index);
  };
  return (
    <div ref={ref} role="menu" aria-label={label} className="p2-menu" style={{ left: at.x, top: at.y }} onKeyDown={onKeyDown}>
      {items.map((item, i) => (
        <Fragment key={item.id}>
          {item.separatorBefore === true && <div role="separator" className="p2-menu-separator" />}
          <button
            type="button"
            role="menuitem"
            tabIndex={i === active ? 0 : -1}
            disabled={disabled[i]}
            className={joinClass('p2-menu-item', item.danger === true && 'p2-menu-item-danger')}
            onPointerEnter={() => { if (!disabled[i]) setActive(i); }}
            onClick={() => { onSelect(item.id); onClose(); }}
          >
            {item.label}
          </button>
        </Fragment>
      ))}
    </div>
  );
}
